@reference "tailwindcss";

:host {
	@apply block w-full min-w-0;
	/* Ensure no constraints on mobile */
	max-width: 100%;
	box-sizing: border-box;
}

/* Date input container needs special handling on mobile */
.date-input-container {
	@apply w-full;
	/* Explicit width for mobile Safari */
	width: 100% !important;
	max-width: 100% !important;
	min-width: 0;
}

/* The prefix/suffix glyph tracks the control ladder. schmancy-icon pins its own
   `:host { font-size: 24px }`, which inheritance can never beat, so the size has
   to be specified ON the slotted element — and only a ::slotted() rule from this
   tree reaches it (no Tailwind utility crosses that boundary). This tree is
   outer to the icon's, so it wins; a consumer class on the icon is a class
   selector and outranks this type selector, so an explicit size still overrides.
   The custom property is written by render()'s inline style on the slot. */
::slotted(schmancy-icon) {
	font-size: var(--schmancy-input-adornment-size, 24px);
}

input {
	/* Prevent Safari zoom on focus */
	font-size: 16px;
	width: -webkit-fill-available;
	/* Smooth transitions for luminous focus */
	transition:
		box-shadow 300ms cubic-bezier(0.34, 1.56, 0.64, 1),
		border-color 200ms ease;
}

:host([otp]) input {
	font-variant-numeric: tabular-nums;
	letter-spacing: 0.35em;
}

/* Luminous focus glow — light-based depth on focus */
input:focus {
	box-shadow:
		0 0 0 1px var(--schmancy-sys-color-secondary-default),
		0 0 16px -4px color-mix(in srgb, var(--schmancy-sys-color-secondary-default) 20%, transparent);
}

/* Luminous error glow — red glow on error focus */
input[aria-invalid='true'] {
	box-shadow: 0 0 12px -4px color-mix(in srgb, var(--schmancy-sys-color-error-default) 15%, transparent);
}
input[aria-invalid='true']:focus {
	box-shadow:
		0 0 0 1px var(--schmancy-sys-color-error-default),
		0 0 16px -4px color-mix(in srgb, var(--schmancy-sys-color-error-default) 25%, transparent);
}

/* The focused label takes `secondary` — the accent the ring above already
   paints — rather than M3's literal `primary`, so focus reads as one state
   instead of a label and a ring naming two different roles.

   It lives here and not in `labelClasses` because no Tailwind variant reaches
   it: the label is rendered BEFORE the input's wrapper, so neither `peer-*` nor
   a sibling combinator can see the focus, and `:host()` is not a variant. Error
   and disabled are excluded IN THE SELECTOR rather than left to the cascade,
   because this unlayered rule outranks the `text-error-default` utility the
   label carries at (0,1,0) — so a focused invalid field would otherwise paint
   the focus colour over its own error.

   The `actions` region is subtracted for the same reason the token is
   `secondary` at all. `:focus-within` on the host also matches when a SLOTTED
   action is focused — a slotted control is a child of the host — and that is
   the one focus the ring never follows, because the ring is `input:focus`. Left
   in, tabbing from the value to the button beside it would darken the ring and
   light the label in the same beat: the exact disagreement this colour was
   chosen to prevent. Subtracting the region rather than enumerating the
   controls keeps the input, the picker <button> and the clear control — whose
   own focus sits one shadow root deeper, which `:focus-within` reaches and
   `:focus` does not — plus anything later added inside the chrome. `~ *` and
   not `~ div`: the wrapper's tag is layout, and a rule that stops matching when
   it changes fails open, straight back to the bug. */
:host(:focus-within:not([error]):not([disabled])) label:not(:has(~ * [part='actions']:focus-within)) {
	color: var(--schmancy-sys-color-secondary-default);
}

@media (prefers-reduced-motion: reduce) {
	input {
		transition: none;
	}
}

/* No glow, which a box this small has no room for: a ground and an inset ring say where the typing goes. */
:host([variant='bare']) input:focus {
	box-shadow: inset 0 0 0 1px var(--schmancy-sys-color-secondary-default);
}

:host([variant='bare']) input {
	border-radius: var(--schmancy-radius-xs, 0.4375rem);
}

:host([density='compact']) input {
	height: 2rem;
	padding: 0 0.25rem;
	font-size: 0.75rem;
	line-height: 2rem;
}

@container (min-width: 36rem) {
	:host([density='compact']) input {
		height: 1.5rem;
		font-size: 0.6875rem;
		line-height: 1.5rem;
	}
}

/* Remove number input spinners */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	appearance: none;
	@apply m-0;
}

input[type='number'] {
	-moz-appearance: textfield;
	appearance: textfield;
}

/* Autofill styling */
@keyframes onAutoFillStart {
	from {
	}
	to {
	}
}

input:-webkit-autofill {
	animation-name: onAutoFillStart;
	/* Use box-shadow trick to override autofill background */
	-webkit-box-shadow: 0 0 0 30px var(--schmancy-sys-color-surface-highest) inset !important;
	-webkit-text-fill-color: var(--schmancy-sys-color-surface-on) !important;
}

/* Simplified autofill for all browsers */
input:autofill {
	background-color: var(--schmancy-sys-color-surface-highest) !important;
	color: var(--schmancy-sys-color-surface-on) !important;
}

/* Date and datetime inputs need special handling */
input[type='date'],
input[type='datetime-local'],
input[type='time'],
input[type='month'],
input[type='week'] {
	/* Ensure consistent height and padding */
	@apply block w-full;
	/* Reset WebKit styling */
	-webkit-appearance: none;
	appearance: none;
	/* Ensure text doesn't get cut off */
	line-height: inherit;
	/* Force full width on mobile */
	width: 100% !important;
	min-width: 100%;
	max-width: 100%;
	/* Remove any default margins */
	margin: 0;
	/* !important defends against native date/time picker chrome reasserting its
	   own corner shape after appearance:none — kept in sync with fieldChromeClasses'
	   rounded-lg (see input-chrome.ts) rather than the class map, which this
	   unlayered rule always outranks regardless of !important. */
	border-radius: var(--schmancy-radius-xl, 0.875rem) !important;
	overflow: hidden;
}

/* WebKit date/time edit container - this is what needs centering */
input::-webkit-datetime-edit,
input::-webkit-datetime-edit-fields-wrapper {
	/* Center the date/time fields vertically */
	display: flex;
	align-items: center;
	height: 100%;
	/* Ensure full width on mobile */
	width: 100%;
	padding: 0;
	margin: 0;
}

input::-webkit-datetime-edit {
	/* Ensure the edit container fills available space */
	flex: 1;
	width: 100%;
}

input::-webkit-datetime-edit-fields-wrapper {
	/* Make fields wrapper expand */
	flex: 1;
	width: 100%;
	/* Segments pack left (flex-start, the default). `space-between` is only
	   right for a wrapper sized to its content; this one is forced to the
	   field's full width just above, so it spread "15 . 08 . 2026 , 18 : 30"
	   edge to edge across a 576px field and the instant stopped reading as one
	   value. */
	justify-content: flex-start;
}

input::-webkit-datetime-edit-text {
	/* Ensure separators are also centered */
	display: flex;
	align-items: center;
}

/* Individual date fields */
input::-webkit-datetime-edit-day-field,
input::-webkit-datetime-edit-month-field,
input::-webkit-datetime-edit-year-field,
input::-webkit-datetime-edit-hour-field,
input::-webkit-datetime-edit-minute-field,
input::-webkit-datetime-edit-second-field,
input::-webkit-datetime-edit-millisecond-field,
input::-webkit-datetime-edit-meridiem-field {
	/* Ensure fields don't shrink */
	flex-shrink: 0;
	padding: 0 2px;
}

/* Safari date input calendar icon */
input[type='date']::-webkit-calendar-picker-indicator,
input[type='datetime-local']::-webkit-calendar-picker-indicator,
input[type='time']::-webkit-calendar-picker-indicator,
input[type='month']::-webkit-calendar-picker-indicator,
input[type='week']::-webkit-calendar-picker-indicator {
	/* Enlarge the calendar glyph for presence; scale from center so it keeps
	   its right-edge position. */
	opacity: 0.6;
	cursor: pointer;
	transform: scale(1.35);
	transform-origin: center;
}

/* Firefox date input styling */
@-moz-document url-prefix() {
	input[type='date'],
	input[type='datetime-local'],
	input[type='time'] {
		/* Firefox needs explicit height */
		min-height: inherit;
	}
}

/* iOS Safari specific fixes */
@supports (-webkit-touch-callout: none) {
	input {
		/* iOS zooms the whole page on focus of a control whose computed
		   font-size is under 16px; the size-ladder's inline style (input.ts)
		   sets 10/12/14px for xxs/xs/sm/md and only !important can outrank an
		   inline style. */
		font-size: 16px !important;
	}

	input[type='date'],
	input[type='datetime-local'],
	input[type='time'],
	input[type='month'],
	input[type='week'] {
		/* iOS Safari needs explicit width */
		width: -webkit-fill-available !important;
		width: fill-available !important;
		/* appearance:none is what drops the native date chrome; the corner stays
		   unset here so it keeps resolving through the ladder token above. */
		-webkit-appearance: none;
		appearance: none;
		/* Remove default padding that causes issues */
		padding-left: 12px !important;
		padding-right: 12px !important;
	}

	/* Target shadow DOM elements on iOS */
	input::-webkit-datetime-edit {
		/* Force full width */
		width: 100% !important;
		padding-left: 0 !important;
		padding-right: 0 !important;
	}

	input::-webkit-datetime-edit-fields-wrapper {
		/* Ensure proper spacing on mobile */
		width: 100% !important;
		padding: 0 !important;
		margin: 0 !important;
	}
}
